import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box, Card, Chip, Divider, Stack, Typography,
} from '@mui/material';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';

import { MONO_FONT, TOKENS } from '../../../theme/muiTheme';
import { formatPercent } from '../../../shared/utils/number';

export default function DashboardKpiCard({
  label,
  value,
  formattedValue,
  previousValue,
  previousYear,
  icon: Icon,
  tone,
  changeLabel,
  noComparisonLabel,
}) {
  const theme = useTheme();
  const hasPreviousValue = Number.isFinite(previousValue) && previousValue !== 0;
  const delta = hasPreviousValue
    ? ((value - previousValue) / previousValue) * 100
    : null;
  const isDecrease = delta !== null && delta < 0;
  const changeColor = isDecrease ? TOKENS.success : TOKENS.warning;
  const changeBackground = isDecrease ? TOKENS.successBg : TOKENS.warningBg;
  const ChangeIcon = delta === null
    ? RemoveRoundedIcon
    : (isDecrease ? TrendingDownRoundedIcon : TrendingUpRoundedIcon);

  return (
    <Card
      component="article"
      sx={{
        height: '100%',
        minHeight: 190,
        overflow: 'hidden',
        position: 'relative',
        borderColor: theme => alpha(tone.color, theme.palette.mode === 'dark' ? 0.5 : 0.32),
        background: theme => theme.palette.mode === 'dark'
          ? `linear-gradient(150deg, ${alpha(tone.color, 0.22)} 0%, ${alpha(tone.color, 0.06)} 62%)`
          : `linear-gradient(150deg, ${alpha(tone.color, 0.1)} 0%, ${alpha(tone.color, 0.02)} 62%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '0 0 auto',
          height: 4,
          bgcolor: tone.color,
        },
      }}
    >
      <Stack spacing={1.5} sx={{ height: '100%', p: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
          <Typography
            component="h2"
            variant="subtitle1"
            sx={{ fontSize: { xs: 17, md: 18 }, fontWeight: 750, lineHeight: 1.3, pt: 0.25 }}
          >
            {label}
          </Typography>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: theme.palette.mode === 'dark' ? alpha(tone.color, 0.2) : tone.bg,
              borderRadius: 2.5,
              color: tone.color,
              display: 'flex',
              flex: '0 0 auto',
              height: 40,
              justifyContent: 'center',
              width: 40,
            }}
          >
            <Icon fontSize="small" />
          </Box>
        </Stack>

        <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: theme.palette.mode === 'dark' ? tone.color : tone.dark,
                fontFamily: MONO_FONT,
                fontSize: { xs: 28, md: 34 },
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 700,
                letterSpacing: '-0.01em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formattedValue}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', flexShrink: 0, fontWeight: 600, fontSize: 15 }}>
              tCO₂e
            </Typography>
        </Stack>

        <Divider sx={{ mt: 'auto !important' }} />
        <Chip
          icon={<ChangeIcon />}
          label={delta === null
            ? noComparisonLabel
            : changeLabel(formatPercent(Math.abs(delta)), previousYear)}
          size="small"
          sx={{
            alignSelf: 'flex-start',
            bgcolor: theme.palette.mode === 'dark' ? alpha(changeColor, 0.18) : changeBackground,
            color: theme.palette.mode === 'dark' ? alpha(changeColor, 0.95) : changeColor,
            '& .MuiChip-icon': { color: 'inherit', fontSize: 17 },
          }}
        />
      </Stack>
    </Card>
  );
}
